/**
 * EGY CÉG KAPCSOLATTARTÓINAK ÁLLAPOTA — a Cégek szelet alatt (#32).
 *
 * A `useUsers` mintájára: a `fail` ág, az `alive` őr, az `inFlight` zár és a
 * `latestLoad` sorszám ugyanaz a négy fogás, ugyanazokkal az okokkal. Egy
 * negyedik horog-minta itt nem nyerne semmit.
 *
 * ═══ KÉT ELTÉRÉS, MIND A KETTŐ SZÁNDÉKOS ═══
 *
 *   • MINDEN ÚTVONAL A CÉG ALÁ MEGY (`/companies/:companyId/contacts`). A
 *     `companyId` nem a törzsben utazik — a backend DTO-ja el sem fogadná
 *     (`create-company-contact.dto.ts`).
 *   • A CÉGEK LISTÁJÁT NEM TÖLTI BE. A `useUsers`-nek azért kellett, mert a
 *     „mit lát" oszlopban cégnevet mutat; itt EGY cégről van szó, és annak a
 *     sorát a hívó már a kezében tartja.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { ApiError, api } from '@/lib/api-client';

/** Egy sor — a backend `CompanyContactRow`-ja. */
export interface ContactRow {
  id: string;
  email: string;
  name: string;
  companyId: string;
  active: boolean;
  /** `null`: még soha nem lépett be — a meghívó függőben. */
  lastLoginAt: string | null;
  createdAt: string;
}

/**
 * AMIT AZ ŰRLAP KÜLD. ⚠️ SEM `role`, SEM `scope`, SEM `companyId` — mind a
 * három KÖVETKEZMÉNY, nem bevitt adat. A DTO fejléce vezeti le, miért.
 */
export interface ContactInput {
  email: string;
  name: string;
}

type InvitationOutcome = 'sent' | 'failed';

/** Egy meghívó sorsa — a lista tetején mondja ki, mi történt. */
export interface InvitationNotice {
  outcome: InvitationOutcome;
  email: string;
}

export type ContactsStatus =
  | 'loading'
  | 'ready'
  | 'loadFailed'
  | 'forbidden'
  | 'saving'
  | 'saved'
  | 'failed'
  | 'invalid'
  | 'conflict'
  /** Írásra jött 403 — a szerver megmondja, miért nem. */
  | 'refused';

export interface ContactsState {
  status: ContactsStatus;
  contacts: ContactRow[];
  /** A szerver üzenete a 400-as, a 403-as, a 404-es és a 409-es ághoz. */
  detail: string;
  invitation: InvitationNotice | null;
  create: (input: ContactInput, onSaved: () => void) => void;
  setActive: (userId: string, active: boolean) => void;
  resendInvitation: (contact: ContactRow) => void;
  dismissInvitation: () => void;
  /** A „Mégse" hívja — `useUsers.clearWriteError`, ugyanazért. */
  clearWriteError: () => void;
  reload: () => void;
}

/** Az írás hibaágai — ezeket viszi el a `clearWriteError`. A `forbidden` NEM: az a lapé. */
const WRITE_ERRORS: ReadonlySet<ContactsStatus> = new Set([
  'failed',
  'invalid',
  'conflict',
  'refused',
]);

/**
 * Egy írás hibája → a lap állapota. `null`: lejárt munkamenet.
 *
 * ⚠️ A 404 IS A SZERVER MONDATÁT MUTATJA (`conflict` ágon). Ez az ÚNIKUM a
 * `useUsers`-hez képest: ott nincs 404-es út, itt van — „ennek a cégnek nincs
 * ilyen kapcsolattartója" —, és ez a mondat pontosan megmondja a teendőt:
 * tölts újra, mert a lista elavult.
 */
function writeFailure(caught: unknown): { status: ContactsStatus; message: string } | null {
  const httpStatus = caught instanceof ApiError ? caught.status : 0;
  const message = caught instanceof ApiError ? caught.message : '';

  switch (httpStatus) {
    case 401:
      return null;
    case 403:
      return { status: 'refused', message };
    case 400:
      return { status: 'invalid', message };
    case 404:
    case 409:
      // ⚠️ A SZERVER MONDATA, SZÓ SZERINT: „ezzel az e-mail címmel már van
      // felhasználó", „a cég inaktív", „már beállította a jelszavát" — a
      // teendő mindháromban más.
      return { status: 'conflict', message };
    default:
      return { status: 'failed', message };
  }
}

export function useCompanyContacts(companyId: string): ContactsState {
  const { signOut } = useAuth();
  const [status, setStatus] = useState<ContactsStatus>('loading');
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [detail, setDetail] = useState('');
  const [invitation, setInvitation] = useState<InvitationNotice | null>(null);

  /**
   * ⚠️ A ZÁR REFERENCIÁN ÜL, NEM ÁLLAPOTON — `useUsers` fejléce. Itt egy dupla
   * felvétel a SAJÁT első mentésre adna „foglalt e-mail" 409-et, és a
   * kapcsolattartó két meghívót kapna.
   */
  const inFlight = useRef(false);

  /** A lecsatolt nézet nem ír állapotot. */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /** ⚠️ CSAK A LEGUTÓBBI BETÖLTÉS ÍRHAT — `useUsers` `latestLoad`, ugyanazért. */
  const latestLoad = useRef(0);

  const reload = useCallback(() => {
    latestLoad.current += 1;
    const load = latestLoad.current;
    setStatus('loading');

    void api<ContactRow[]>(`/companies/${companyId}/contacts`)
      .then((list) => {
        if (!alive.current || load !== latestLoad.current) {
          return;
        }
        setContacts(list);
        setStatus('ready');
      })
      .catch((caught: unknown) => {
        if (!alive.current || load !== latestLoad.current) {
          return;
        }
        const httpStatus = caught instanceof ApiError ? caught.status : 0;
        if (httpStatus === 401) {
          signOut('expired');
          return;
        }
        setStatus(httpStatus === 403 ? 'forbidden' : 'loadFailed');
      });
  }, [companyId, signOut]);

  useEffect(() => {
    reload();
  }, [reload]);

  /**
   * A HÁROM ÍRÁSI ÚT KÖZÖS TÖRZSE. A siker után újratöltünk, nem a helyi
   * tömböt írjuk át: a szerver számol (a kisbetűsített e-mail, a rendezés), és
   * egy helyi összefésülés ebben csendben elcsúszna.
   */
  const write = useCallback(
    <T>(path: string, method: 'POST' | 'PUT', body: unknown, onSuccess?: (result: T) => void) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      setStatus('saving');
      setDetail('');

      void api<T>(path, {
        method,
        body: body === undefined ? undefined : JSON.stringify(body),
      })
        .then((result) => {
          if (!alive.current) {
            return;
          }
          onSuccess?.(result);
          setStatus('saved');
          reload();
        })
        .catch((caught: unknown) => {
          if (!alive.current) {
            return;
          }
          const failure = writeFailure(caught);
          if (failure === null) {
            signOut('expired');
            return;
          }
          setStatus(failure.status);
          setDetail(failure.message);
        })
        .finally(() => {
          inFlight.current = false;
        });
    },
    [reload, signOut]
  );

  const create = useCallback(
    (input: ContactInput, onSaved: () => void) =>
      write<ContactRow & { invitation: InvitationOutcome }>(
        `/companies/${companyId}/contacts`,
        'POST',
        input,
        (result) => {
          // ⚠️ A `failed` NEM A FELVÉTEL HIBÁJA: a kapcsolattartó létrejött,
          // csak a levél nem ment ki. Az űrlap ezért ZÁRUL, és a lista teteje
          // mondja meg, hogy a sorában újra kell küldeni.
          setInvitation({ outcome: result.invitation, email: result.email });
          onSaved();
        }
      ),
    [companyId, write]
  );

  /**
   * ⚠️ KÜLÖN ÚTVONAL, NEM EGY ŰRLAP RÉSZE — ugyanaz az ok, mint a cégnél és a
   * felhasználónál: egy DÖNTÉS nem eshet bele egy mentésbe.
   */
  const setActive = useCallback(
    (userId: string, active: boolean) =>
      write(`/companies/${companyId}/contacts/${userId}/active`, 'PUT', { active }),
    [companyId, write]
  );

  const resendInvitation = useCallback(
    (contact: ContactRow) =>
      write<{ invitation: InvitationOutcome }>(
        `/companies/${companyId}/contacts/${contact.id}/invitation`,
        'POST',
        undefined,
        (result) => setInvitation({ outcome: result.invitation, email: contact.email })
      ),
    [companyId, write]
  );

  const dismissInvitation = useCallback(() => setInvitation(null), []);

  const clearWriteError = useCallback(() => {
    setStatus((current) => (WRITE_ERRORS.has(current) ? 'ready' : current));
    setDetail('');
  }, []);

  return {
    status,
    contacts,
    detail,
    invitation,
    create,
    setActive,
    resendInvitation,
    dismissInvitation,
    clearWriteError,
    reload,
  };
}
